import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Grid, Icon, Tab, Tabs, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useForm } from 'react-hook-form';
import { CustomPagination, checkInfosProduto } from 'utils/functions';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Button from 'components/Button';
import Input from 'components/Input';
import localeText from 'utils/localeText';

const ItemModal = ({ item, onSubmit }) => {
  const { drops } = useContext(DropsContext);
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState(0);
  const defaultValues = useMemo(
    () => ({
      descricao: item?.descricao || '',
      quantidade: item?.quantidade || '',
      unidade_medida_id: item?.unidade_medida_id || null,
      referencia: item?.referencia || '',
      desconto1_pc: item?.desconto1_pc || '',
      desconto_vlr: item?.desconto_vlr || '',
      przent: item?.przent || null,
      preco: item?.preco || '',
      planoconta_id: item?.planoconta_id || null,
      ccusto_id: item?.ccusto_id || null,
      aplicacao_id: item?.aplicacao_id || null,
      frota_id: item?.frota_id || null,
      infadc: item?.infadc || '',
      Estruturas: item?.Estruturas || [],
    }),
    [item]
  );
  const { control, watch, setValue, handleSubmit } = useForm({ defaultValues });
  const watchedValues = watch(['desconto1_pc', 'preco', 'quantidade']);

  const produtoMap = useMemo(() => {
    const map = new Map();
    drops?.Produto?.forEach((p) => map?.set(String(p?.value), p?.label));
    return map;
  }, [drops?.Produto]);

  const produtoOptions = useMemo(
    () => [
      { value: null, label: 'NENHUM' },
      ...(drops?.Produto?.map((p) => ({
        value: p?.value,
        label: p?.label,
      })) ?? []),
    ],
    [drops?.Produto]
  );

  const produtoBaseOptions = useMemo(
    () => [
      { value: null, label: 'NENHUM' },
      ...(drops?.Produto?.filter(
        (f) => f?.tipolinha === '01-Matéria prima'
      )?.map((p) => ({
        value: p?.value,
        label: p?.label,
      })) ?? []),
    ],
    [drops?.Produto]
  );

  const formatProduto = ({ value }) => {
    if (value == null) return '';
    return produtoMap.get(String(value)) ?? String(value);
  };

  useEffect(() => {
    const [desconto1_pc, preco, quantidade] = watchedValues;
    const desconto_vlr = desconto1_pc * preco * quantidade;
    setValue(
      'desconto_vlr',
      desconto_vlr !== 0 ? desconto_vlr * -0.01 : desconto_vlr
    );
  }, [watchedValues, setValue]);

  const handleTabChange = (_, t) => setTab(t);

  const handlePreco = (arr = []) =>
    arr.reduce((acc, curr) => {
      const preco = curr?.preco || 0;
      const qtd = curr?.quantidade || 0;

      const medida =
        (curr?.comprimento || 0) * (curr?.largura || 0) > 0
          ? (curr?.comprimento || 0) * (curr?.largura || 0)
          : 1;

      return acc + preco * qtd * medida;
    }, 0);

  const onAddEstrutura = (values) => {
    const estruturas = values?.Estruturas ?? [];
    const maxSeq = estruturas?.length
      ? Math.max(...estruturas?.map((e) => Number(e?.sequencia) || 0))
      : 0;

    setValue('Estruturas', [
      ...estruturas,
      {
        index: estruturas.length,
        sequencia: maxSeq + 1,
        componente_id: null,
        quantidade: 1,
        comprimento: 0,
        largura: 0,
        altura: 0,
        produtobase_id: null,
        observacao: null,
      },
    ]);
  };

  const onDeleteEstrutura = (id) => {
    const atualizadas = (watch('Estruturas') || [])
      ?.filter((item) => item?.index !== id)
      ?.map((m, index) => ({ ...m, index }));
    setValue('Estruturas', atualizadas);
    if (atualizadas?.some((s) => Boolean(s?.produtobase_id))) {
      const totalPreco = handlePreco(atualizadas);
      setValue('preco', parseFloat(totalPreco?.toFixed(2)));
    }
  };

  const onCellEditCommit = async ({ id, field, value }) => {
    const estruturas = watch('Estruturas') || [];
    let preco = 0;
    let produto = null;
    const idNum = Number(value);

    if (Number.isFinite(idNum) && idNum > 0) {
      produto = await checkInfosProduto({ produto_id: idNum });
      if (field === 'componente_id') {
        const atualizadas = estruturas.map((item) => {
          if (item?.index !== id) return item;
          return {
            ...item,
            componente_id: idNum,
            sigla: produto?.UnidadeMedida?.sigla ?? '',
          };
        });

        setValue('Estruturas', atualizadas);
        return;
      }
      if (field === 'produtobase_id') {
        preco = produto?.preco || 0;

        const replicar = window.confirm(
          'Deseja replicar este Produto Base para todos os itens com a mesma unidade de medida?'
        );

        const atualizadas = estruturas.map((item) => {
          if (replicar) {
            if (item?.sigla === estruturas.find((e) => e.index === id)?.sigla) {
              return { ...item, produtobase_id: idNum, preco };
            }
          }

          if (item?.index === id) {
            return { ...item, produtobase_id: idNum, preco };
          }

          return item;
        });

        if (atualizadas?.some((s) => Boolean(s?.produtobase_id))) {
          const totalPreco = handlePreco(atualizadas);
          setValue('preco', parseFloat(totalPreco?.toFixed(2)));
        }

        setValue('Estruturas', atualizadas);
        return;
      }
    }

    const atualizadas = estruturas.map((item) => {
      if (item?.index !== id) return item;
      return { ...item, [field]: value };
    });

    if (atualizadas?.some((s) => Boolean(s?.produtobase_id))) {
      const totalPreco = handlePreco(atualizadas);
      setValue('preco', parseFloat(totalPreco?.toFixed(2)));
    }

    setValue('Estruturas', atualizadas);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" component="div" align="center">
          {item?.descricao}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        display={item?.Estruturas?.length > 0 ? 'block' : 'none'}
      >
        <Tabs value={tab} onChange={handleTabChange} variant="fullWidth">
          <Tab label="Informações Gerais" />
          <Tab label="Estrutura" />
        </Tabs>
      </Grid>
      <Grid item xs={12} display={tab === 0 ? 'flex' : 'none'}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Input name="descricao" control={control} label="Descrição" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask name="quantidade" control={control} label="Quantidade" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Dropdown
              name="unidade_medida_id"
              control={control}
              label="Unidade de Medida"
              options={drops?.UnidadeMedida}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Input name="referencia" control={control} label="Referência" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask
              name="desconto1_pc"
              control={control}
              label="% Desconto"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask
              name="preco"
              control={control}
              label="Preço"
              disabled={
                user?.permissoes?.block_altera_precos || item?.block_preco
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputMask
              name="przent"
              control={control}
              label="Prazo de Entrega"
              type="date"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="planoconta_id"
              control={control}
              label="Plano de Conta"
              options={drops?.Planoconta?.filter(
                (p) => p?.operacional === 'SIM'
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="ccusto_id"
              control={control}
              label="Centro de Custo"
              options={drops?.Ccusto}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="aplicacao_id"
              control={control}
              label="Aplicação"
              options={drops?.Aplicacao}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              name="frota_id"
              control={control}
              label="Frota"
              options={drops?.Frota}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              name="infadc"
              control={control}
              label="Informações Adicionais"
              multiline
              rows={5}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} display={tab === 1 ? 'flex' : 'none'}>
        <Grid container spacing={2}>
          <Grid item xs={12} textAlign="center">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSubmit(onAddEstrutura)}
            >
              Adicionar Estrutura
            </Button>
          </Grid>
          <Grid item xs={12}>
            <DataGrid
              rows={watch('Estruturas') || []}
              columns={[
                {
                  field: 'actions',
                  headerName: 'Ações',
                  type: 'actions',
                  flex: 0,
                  getActions: ({ id }) => [
                    <GridActionsCellItem
                      icon={<Icon>delete</Icon>}
                      label="Excluir"
                      onClick={() => onDeleteEstrutura(id)}
                    />,
                  ],
                },
                {
                  field: 'sequencia',
                  headerName: 'Sequência',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'componente_id',
                  headerName: 'Componente',
                  type: 'singleSelect',
                  editable: true,
                  flex: 1,
                  minWidth: 300,
                  sortable: false,
                  valueOptions: produtoOptions,
                  valueFormatter: formatProduto,
                },
                {
                  field: 'sigla',
                  headerName: 'Unidade de Medida',
                  width: 150,
                  sortable: false,
                  editable: false,
                },
                {
                  field: 'quantidade',
                  headerName: 'Quantidade',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'comprimento',
                  headerName: 'Comprimento',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'largura',
                  headerName: 'Largura',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'altura',
                  headerName: 'Altura',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: true,
                },
                {
                  field: 'medida',
                  headerName: 'Medida',
                  type: 'number',
                  width: 120,
                  sortable: false,
                  editable: false,
                  valueGetter: ({ row }) => {
                    const largura = Number(row?.largura) || 0;
                    const comprimento = Number(row?.comprimento) || 0;
                    return largura * comprimento;
                  },
                },
                {
                  field: 'produtobase_id',
                  headerName: 'Produto Base',
                  type: 'singleSelect',
                  editable: true,
                  flex: 1,
                  minWidth: 300,
                  sortable: false,
                  valueOptions: produtoBaseOptions,
                  valueFormatter: formatProduto,
                },
                {
                  field: 'observacao',
                  headerName: 'Observação',
                  width: 300,
                  sortable: false,
                  editable: true,
                },
              ]}
              autoHeight
              pagination
              density="compact"
              disableColumnMenu
              showCellRightBorder
              showColumnRightBorder
              disableSelectionOnClick
              localeText={localeText}
              getRowId={(row) => row?.index}
              onCellEditCommit={onCellEditCommit}
              components={{ Pagination: CustomPagination }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} textAlign="center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
        >
          Salvar
        </Button>
      </Grid>
    </Grid>
  );
};
export default ItemModal;
